import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const uid = () =>
  (crypto.randomUUID && crypto.randomUUID()) ||
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });

export function getAnonId() {
  let id = localStorage.getItem("fanly_anon_id");
  if (!id) {
    id = uid();
    localStorage.setItem("fanly_anon_id", id);
  }
  return id;
}

export function captureAcquisition() {
  const params = new URLSearchParams(window.location.search);
  const existing = JSON.parse(localStorage.getItem("fanly_acq") || "null");
  if (existing) return existing;
  const acq = {
    source: params.get("source") || params.get("utm_source") || params.get("src") || null,
    campaign: params.get("campaign") || params.get("utm_campaign") || null,
    content: params.get("content") || params.get("utm_content") || null,
    trigger_id: params.get("trigger") || params.get("trigger_id") || null,
    broadcast_id: params.get("broadcast") || null,
    referrer: document.referrer || null,
  };
  localStorage.setItem("fanly_acq", JSON.stringify(acq));
  return acq;
}

// CURRENT-visit attribution, read fresh from the URL every load (e.g. a return via an
// email broadcast link). Original acquisition stays sticky in fanly_acq / on the fan record.
export function currentVisit() {
  const params = new URLSearchParams(window.location.search);
  return {
    source: params.get("source") || params.get("utm_source") || params.get("src") || null,
    campaign: params.get("campaign") || params.get("utm_campaign") || null,
    content: params.get("content") || params.get("utm_content") || null,
    trigger_id: params.get("trigger") || params.get("trigger_id") || null,
    broadcast_id: params.get("broadcast") || null,
    referrer: document.referrer || null,
  };
}

export function getAcquisition() {
  return JSON.parse(localStorage.getItem("fanly_acq") || "{}");
}

export function getFanToken() {
  return localStorage.getItem("fanly_fan_token");
}
export function setFanToken(t) {
  localStorage.setItem("fanly_fan_token", t);
}

let _lastSlug = null;
// Ad-review crawlers / link previewers execute JS and were being counted as visitors.
const IS_BOT = /meta-externalads|facebookexternalhit|facebookcatalog|Googlebot|bingbot|AdsBot|crawler|spider|HeadlessChrome|Lighthouse|Prerender/i
  .test(typeof navigator !== "undefined" ? navigator.userAgent || "" : "");
export function getLang() {
  const s = sessionStorage.getItem("fanly_lang_campaign") || localStorage.getItem("fanly_lang");
  return s || "en";
}
export async function setFanLanguage(language) {
  const token = getFanToken();
  if (!token || !_lastSlug) return;
  await axios.post(`${API}/fan/language`, { anon_id: getAnonId(), project_slug: _lastSlug, language },
    { headers: { Authorization: `Bearer ${token}` } });
}
export async function touchSessionLanguage(language) {
  if (!_lastSlug) return;
  await axios.post(`${API}/sessions`, { anon_id: getAnonId(), project_slug: _lastSlug, language });
}

// Events wait (max 4s) for the session doc so they get attribution + test flag.
let _sessionReady = Promise.resolve();
export async function initSession(slug) {
  _lastSlug = slug;
  if (IS_BOT) return;
  const anon_id = getAnonId();
  captureAcquisition();            // stick original acquisition (used at join time)
  const visit = currentVisit();    // current-visit attribution (email broadcast, etc.)
  // Internal QA marker: ?qa=fanly marks this browser's sessions as test data (sticky).
  try { if (new URLSearchParams(window.location.search).get("qa") === "fanly") localStorage.setItem("fanly_qa", "1"); } catch (e) {}
  const qa = localStorage.getItem("fanly_qa") === "1";
  const dev = getDeviceInfo();
  const req = axios.post(`${API}/sessions`, {
    anon_id,
    project_slug: slug,
    ...visit,
    qa,
    language: getLang(),
    user_agent: navigator.userAgent,
    browser: dev.browser, webview: dev.webview, os: dev.os, is_inapp: dev.is_inapp,
  }).catch(() => {});
  _sessionReady = Promise.race([req, new Promise((r) => setTimeout(r, 4000))]);
  await req;
}

// Best-effort device / in-app-webview fingerprint (for preview-failure diagnostics).
// IMPORTANT precedence: check the SPECIFIC app token (Instagram/TikTok) BEFORE Facebook.
// Instagram Android UAs contain the literal "Instagram" and do NOT contain FB4A/FBAN/FBAV,
// while Facebook Android in-app contains FB_IAB/FB4A/FBAV — so IG is never mislabeled as FB.
export function getDeviceInfo() {
  const ua = (navigator.userAgent || "");
  const webview =
    /Instagram/i.test(ua) ? "instagram" :
    /musical_ly|BytedanceWebview|TikTok|Trill/i.test(ua) ? "tiktok" :
    /Twitter|TwitterAndroid/i.test(ua) ? "twitter" :
    /Line\//i.test(ua) ? "line" :
    /Snapchat/i.test(ua) ? "snapchat" :
    /Pinterest/i.test(ua) ? "pinterest" :
    /FBAN|FBAV|FB_IAB|FB4A|FBIOS/i.test(ua) ? "facebook" :
    // generic embedded WebView fallback (Android "; wv" or iOS WKWebView without Safari token)
    /; wv\)/i.test(ua) ? "android_webview" :
    (/iPhone|iPad/i.test(ua) && !/Safari/i.test(ua) && !/CriOS|FxiOS/i.test(ua)) ? "ios_webview" : null;
  const os = /iPhone|iPad|iPod/i.test(ua) ? "ios"
    : /Android/i.test(ua) ? "android"
    : /Macintosh/i.test(ua) ? "mac"
    : /Windows/i.test(ua) ? "windows"
    : /Linux/i.test(ua) ? "linux" : "other";
  const browser = webview ? `${webview}_webview`
    : /CriOS/i.test(ua) ? "chrome_ios"
    : /FxiOS|Firefox/i.test(ua) ? "firefox"
    : /Edg\//i.test(ua) ? "edge"
    : /Chrome/i.test(ua) ? "chrome"
    : /Version\/.*Safari/i.test(ua) ? "safari" : "other";
  return { os, browser, webview, is_inapp: !!webview };
}

// Reliable event delivery for mobile / in-app webviews:
// sendBeacon (survives webview suspend / page unload) → fetch keepalive → one light retry.
function _sendEvent(url, body, headers) {
  if (IS_BOT) return Promise.resolve();
  return _sessionReady.then(() => _sendNow(url, body, headers));
}

function _sendNow(url, body, headers) {
  const json = JSON.stringify(body);
  if (!headers && navigator.sendBeacon) {
    try {
      const blob = new Blob([json], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return Promise.resolve();
    } catch (e) {}
  }
  const opts = { method: "POST", headers: { "Content-Type": "application/json", ...(headers || {}) }, body: json, keepalive: true };
  const attempt = () => fetch(url, opts);
  return attempt().catch(() => new Promise((r) => setTimeout(r, 400)).then(() => attempt().catch(() => {})));
}

export function track(slug, type, meta = {}) {
  return _sendEvent(`${API}/events`, { anon_id: getAnonId(), project_slug: slug, type, meta });
}

export function trackAuth(slug, type, meta = {}) {
  return _sendEvent(`${API}/events/auth`, { anon_id: getAnonId(), project_slug: slug, type, meta },
    { Authorization: `Bearer ${getFanToken()}` });
}

export async function fetchProject(slug) {
  const { data } = await axios.get(`${API}/projects/${slug}`, { params: { lang: getLang() } });
  return data;
}

export async function getPreviewUrl(slug, start = null, dur = null) {
  const params = start != null && dur != null ? { start, dur } : {};
  const { data } = await axios.get(`${API}/preview/stream-url/${slug}`, { params });
  return data;
}

export async function getFullUrl(slug, original = false) {
  const { data } = await axios.get(`${API}/full/stream-url/${slug}`, {
    headers: { Authorization: `Bearer ${getFanToken()}` },
    params: original ? { original: true } : {},
  });
  return data.url;
}

export async function submitJoin({ slug, email, first_name, consent }) {
  const acq = getAcquisition();
  const { data } = await axios.post(`${API}/join`, {
    anon_id: getAnonId(),
    project_slug: slug,
    email,
    first_name,
    consent,
    preferred_language: getLang(),
    ...acq,
  });
  // Immediate unlock: persist the fan session so no email round-trip is needed.
  if (data.access_token) setFanToken(data.access_token);
  return data;
}

export async function recordReturnVisit(slug) {
  const token = getFanToken();
  if (!token) return;
  try {
    await axios.post(
      `${API}/fan/visit`,
      { anon_id: getAnonId(), project_slug: slug, type: "landing_view" },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } catch (e) {}
}

export async function notifyNext(slug) {  const { data } = await axios.post(
    `${API}/notify-next`,
    { anon_id: getAnonId(), project_slug: slug, type: "notify_next_drop" },
    { headers: { Authorization: `Bearer ${getFanToken()}` } }
  );
  return data;
}

export async function optInFutureUpdates(slug, extra = {}) {
  const params = new URLSearchParams(window.location.search);
  const meta = {
    language: getLang(),
    source: params.get("source") || params.get("utm_source") || undefined,
    platform: params.get("platform") || params.get("utm_medium") || undefined,
    content_piece: params.get("content_piece") || params.get("utm_content") || undefined,
    ...extra,
  };
  Object.keys(meta).forEach((k) => meta[k] === undefined && delete meta[k]);
  const { data } = await axios.post(
    `${API}/future-updates`,
    { anon_id: getAnonId(), project_slug: slug, type: "future_updates_opt_in", meta },
    { headers: { Authorization: `Bearer ${getFanToken()}` } }
  );
  return data;
}

export function isFanIdentified() {
  return !!getFanToken();
}

export async function getPrefsLink(slug) {
  const { data } = await axios.get(`${API}/fan/prefs-link`, {
    params: { project_slug: slug },
    headers: { Authorization: `Bearer ${getFanToken()}` },
  });
  return data.url;
}

export async function verifyToken(token) {
  // Auth uses a Bearer token stored in localStorage (reliable inside IG/TikTok
  // in-app webviews where third-party cookies are blocked). No credentialed CORS.
  const { data } = await axios.post(`${API}/verify`, { token });
  if (data.access_token) setFanToken(data.access_token);
  return data;
}

export async function accessGateway(token) {
  // Stable gateway: authenticates if the token is valid, or reports 'expired' so the
  // fan can request a fresh link without ever losing release access.
  const { data } = await axios.post(`${API}/access/gateway`, { token });
  if (data.status === "authenticated" && data.access_token) setFanToken(data.access_token);
  return data;
}

export async function accessResend(token) {
  const { data } = await axios.post(`${API}/access/resend`, { token });
  return data;
}


export async function fetchTriggerConfig(slug, content_piece = "", platform = "") {
  const { data } = await axios.get(`${API}/trigger-config/${slug}`, {
    params: { content_piece, platform },
  });
  return data;
}
